import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Form, Input, Select, Button, Card, Row, Col, Radio, Breadcrumb, Grid, message, Spin, Modal, Alert
} from 'antd';
import {
  RightOutlined, UserOutlined, PhoneOutlined, MailOutlined,
  HomeOutlined, SaveOutlined, ShopOutlined, QuestionCircleOutlined,
  CloseOutlined, BankOutlined, IdcardOutlined, CheckCircleOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { clientsService, agencesService } from '../../common/services';
import { useCanSwitchAgence, useUserAgenceId } from '../../common/hooks';

const { Option } = Select;

// Normalisation pour comparer les noms : majuscules, accents retirés, espaces compressés.
const normName = (s) =>
  (s || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/\s+/g, ' ').trim();

const NouveauClient = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [clientType, setClientType] = useState('particulier');
  const [agences, setAgences] = useState([]);
  const [guideOpen, setGuideOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const editing = !!editId;
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const canSwitchAgence = useCanSwitchAgence();
  const userAgenceId = useUserAgenceId();

  // Filet anti-doublon : clients existants portant exactement le même nom
  const [doublons, setDoublons] = useState([]);
  const [nameVals, setNameVals] = useState({ nom: '', prenom: '', raisonSociale: '' });
  const watchedNom = nameVals.nom;
  const watchedPrenom = nameVals.prenom;
  const watchedRaison = nameVals.raisonSociale;

  useEffect(() => {
    if (!canSwitchAgence && userAgenceId && !editId) {
      form.setFieldsValue({ agence: userAgenceId });
    }
  }, [canSwitchAgence, userAgenceId, editId, form]);

  // Recherche (debounce) des clients existants au même nom complet
  useEffect(() => {
    if (editing) { setDoublons([]); return undefined; }
    const fullName = clientType === 'entreprise'
      ? (watchedRaison || '')
      : `${watchedNom || ''} ${watchedPrenom || ''}`.trim();
    const cible = normName(fullName);
    const ready = clientType === 'entreprise'
      ? (watchedRaison || '').trim().length >= 2
      : ((watchedNom || '').trim().length >= 2 && (watchedPrenom || '').trim().length >= 1);
    if (!ready) { setDoublons([]); return undefined; }

    const handle = setTimeout(async () => {
      try {
        const query = clientType === 'entreprise' ? watchedRaison : watchedNom;
        const res = await clientsService.search(query);
        const list = Array.isArray(res) ? res : (res?.data || []);
        const matches = list.filter((c) => {
          if (editId && String(c._id) === String(editId)) return false;
          return normName(`${c.nom || ''} ${c.prenom || ''}`) === cible;
        });
        setDoublons(matches);
      } catch (e) {
        // silencieux : le filet ne doit jamais bloquer la saisie
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [watchedNom, watchedPrenom, watchedRaison, clientType, editing, editId]);

  useEffect(() => {
    const fetchAgences = async () => {
      try {
        const res = await agencesService.getAll();
        setAgences(Array.isArray(res) ? res : res?.data || []);
      } catch (e) {
        console.error(e);
      }
    };
    fetchAgences();

    if (editId) {
      setFetching(true);
      clientsService.getById(editId).then(res => {
        const data = res?.data || res;
        setClientType(data.type || 'particulier');
        form.setFieldsValue({
          ...data,
          telephone: data.telephone?.replace('+225', ''),
          agence: data.agence?._id || data.agence,
        });
      }).catch(() => toast.error('Erreur chargement du client'))
        .finally(() => setFetching(false));
    }
  }, [editId]);

  // Enregistrement effectif (création ou modification)
  const doSave = async (values) => {
    try {
      setLoading(true);
      const data = {
        ...values,
        telephone: `+225${values.telephone}`,
        type: clientType,
      };
      if (clientType === 'entreprise' && values.raisonSociale) {
        data.nom = values.raisonSociale;
      }
      if (editing) {
        await clientsService.update(editId, data);
        toast.success('Client modifie avec succès');
      } else {
        await clientsService.create(data);
        toast.success('Client cree avec succès');
      }
      navigate('/dashboard/clients');
    } catch (error) {
      toast.error(error.message || 'Erreur lors de lenregistrement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (values) => {
    // Filet anti-doublon (création uniquement, non bloquant)
    if (!editing && doublons.length > 0) {
      const enteredTel = `+225${values.telephone}`;
      const memeNumero = doublons.find((d) => d.telephone === enteredTel);

      if (memeNumero) {
        // Même nom ET même numéro → très probablement le même client
        Modal.confirm({
          title: 'Ce client existe déjà',
          icon: <ExclamationCircleOutlined style={{ color: '#C8102E' }} />,
          content: (
            <span>
              Un client porte déjà ce nom <strong>et</strong> ce même numéro ({enteredTel}) —
              il s'agit très probablement du même client.<br /><br />
              S'il s'agit d'un <strong>nouveau</strong> client distinct, annulez et
              saisissez un <strong>numéro de téléphone différent</strong> pour le distinguer.
              Sinon, ouvrez sa fiche existante pour lui ajouter un contrat.
            </span>
          ),
          okText: 'Créer quand même',
          okButtonProps: { danger: true },
          cancelText: 'Annuler et corriger',
          onOk: () => doSave(values),
        });
        return;
      }

      // Même nom mais numéro différent → homonyme probable, on confirme simplement
      Modal.confirm({
        title: 'Nom déjà existant',
        icon: <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />,
        content: (
          <span>
            {doublons.length} client(s) portent déjà ce nom (avec un numéro différent).
            Confirmez-vous la création d'un <strong>nouveau</strong> client ?
          </span>
        ),
        okText: 'Oui, créer',
        cancelText: 'Annuler',
        onOk: () => doSave(values),
      });
      return;
    }

    doSave(values);
  };

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: isMobile ? 12 : 24, paddingRight: isMobile ? 12 : 24 }}>
        <Breadcrumb separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard" style={{ color: '#1A2B5F' }}>Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item><Link to="/dashboard/clients" style={{ color: '#1A2B5F' }}>Clients</Link></Breadcrumb.Item>
          <Breadcrumb.Item><span style={{ color: '#595959' }}>{editing ? 'Modifier' : 'Nouveau'}</span></Breadcrumb.Item>
        </Breadcrumb>

        <Modal
          open={guideOpen}
          onCancel={() => setGuideOpen(false)}
          title={<span><QuestionCircleOutlined style={{ color: '#1A2B5F', marginRight: 8 }} />Guide — Créer un client</span>}
          closeIcon={(
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px',
              background: '#C8102E', border: '1px solid #C8102E', borderRadius: 8,
              fontSize: '0.9rem', fontWeight: 600, color: '#fff',
            }}>
              <CloseOutlined /> Fermer
            </span>
          )}
          footer={<Button type="primary" onClick={() => setGuideOpen(false)}>J'ai compris</Button>}
          width={620}
        >
          <span style={{ color: '#8c8c8c' }}>
            Renseignez les informations du client. Les champs marqués d'un{' '}
            <span style={{ color: '#C8102E' }}>*</span> sont obligatoires.
          </span>
          {(() => {
            const liStyle = {
              display: 'flex', gap: 12, padding: '14px 16px', marginBottom: 10,
              background: '#f6f8fb', border: '1px solid #E8ECF1', borderRadius: 12, lineHeight: 1.5,
            };
            const icStyle = { flexShrink: 0, fontSize: 18, color: '#1A2B5F', marginTop: 2 };
            return (
              <div style={{ marginTop: 16 }}>
                <div style={liStyle}>
                  <IdcardOutlined style={icStyle} />
                  <div>
                    <strong>1 · Type de client</strong> — Choisissez « Particulier » ou
                    « Entreprise ». Pour une entreprise, vous saisirez la <strong>Raison
                    sociale</strong> au lieu du Nom / Prénom(s).
                  </div>
                </div>
                <div style={liStyle}>
                  <UserOutlined style={icStyle} />
                  <div>
                    <strong>2 · Identité</strong> — Renseignez le Nom et le(s) Prénom(s)
                    (ou la Raison sociale). La saisie est mise automatiquement en MAJUSCULES.
                  </div>
                </div>
                <div style={liStyle}>
                  <PhoneOutlined style={icStyle} />
                  <div>
                    <strong>3 · Contact</strong> — Le <strong>Téléphone</strong> est obligatoire :
                    10 chiffres, sans l'indicatif (le +225 est déjà ajouté). L'Email est facultatif.
                  </div>
                </div>
                <div style={liStyle}>
                  <BankOutlined style={icStyle} />
                  <div>
                    <strong>4 · Agence</strong> — Sélectionnez l'agence de rattachement. Si vous
                    n'êtes pas multi-agences, elle est déjà verrouillée sur la vôtre.
                  </div>
                </div>
                <div style={{ ...liStyle, marginBottom: 0 }}>
                  <CheckCircleOutlined style={icStyle} />
                  <div>
                    <strong>5 · Enregistrer</strong> — Cliquez sur « Enregistrer le client ».
                    Il sera ensuite disponible dans la recherche lors de la création d'un contrat.
                  </div>
                </div>
              </div>
            );
          })()}
        </Modal>

        <Spin spinning={fetching} tip="Chargement des donnees du client...">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={(_, all) => setNameVals({ nom: all.nom || '', prenom: all.prenom || '', raisonSociale: all.raisonSociale || '' })}
          size={isMobile ? 'small' : 'middle'}
        >
          <Card
            style={{ borderRadius: 12, marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
            title={<span><UserOutlined style={{ color: '#C8102E', marginRight: 8 }} />{editing ? 'Modifier Client' : 'Nouveau Client'}</span>}
            extra={(
              <Button
                icon={<QuestionCircleOutlined />}
                onClick={() => setGuideOpen(true)}
                style={{ borderRadius: 8, background: '#1A2B5F', borderColor: '#1A2B5F', color: '#fff', fontWeight: 600 }}
              >
                Guide
              </Button>
            )}
          >
            <Row gutter={16}>
              <Col xs={24} style={{ marginBottom: 16 }}>
                <Radio.Group value={clientType} onChange={e => setClientType(e.target.value)} buttonStyle="solid">
                  <Radio.Button value="particulier">Particulier</Radio.Button>
                  <Radio.Button value="entreprise">Entreprise</Radio.Button>
                </Radio.Group>
              </Col>
            </Row>

            {clientType === 'particulier' ? (
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="nom" label="Nom" rules={[{ required: true, message: 'Le nom est obligatoire' }]} normalize={v => v?.toUpperCase()}>
                    <Input autoFocus placeholder="Ex: KOUADIO" prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} style={{ borderRadius: 8 }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="prenom" label="Prénom(s)" rules={[{ required: true, message: 'Le prenom est obligatoire' }]} normalize={v => v?.toUpperCase()}>
                    <Input placeholder="Ex: JEAN MICHEL" style={{ borderRadius: 8 }} />
                  </Form.Item>
                </Col>
              </Row>
            ) : (
              <Row gutter={16}>
                <Col xs={24}>
                  <Form.Item name="raisonSociale" label="Raison sociale" rules={[{ required: true, message: 'La raison sociale est obligatoire' }]} normalize={v => v?.toUpperCase()}>
                    <Input autoFocus placeholder="Ex: SOCIETE EXAMPLE SARL" prefix={<ShopOutlined style={{ color: '#bfbfbf' }} />} style={{ borderRadius: 8 }} />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {!editing && doublons.length > 0 && (
              <Row gutter={16}>
                <Col xs={24} style={{ marginBottom: 16 }}>
                  <Alert
                    type="warning"
                    showIcon
                    style={{ borderRadius: 8 }}
                    message={`${doublons.length} client(s) portent déjà ce nom`}
                    description={(
                      <div>
                        <div style={{ marginBottom: 8 }}>
                          S'il s'agit du <strong>même client</strong>, ouvrez sa fiche pour lui ajouter un contrat
                          (pas besoin de le recréer). Si c'est un client <strong>différent</strong>, saisissez un{' '}
                          <strong>numéro de téléphone différent</strong> pour le distinguer.
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {doublons.map((c) => (
                            <li key={c._id}>
                              {`${c.nom || ''} ${c.prenom || ''}`.trim()} — {c.telephone || '—'}{' '}
                              <Button type="link" size="small" style={{ padding: 0, height: 'auto' }} onClick={() => navigate(`/dashboard/client/${c._id}`)}>
                                Ouvrir la fiche
                              </Button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  />
                </Col>
              </Row>
            )}

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="telephone" label="Téléphone" rules={[{ required: true, message: 'Le téléphone est obligatoire' }, { pattern: /^[0-9]{10}$/, message: 'Le numéro doit contenir 10 chiffres (sans indicatif)' }]}>
                  <Input
                    addonBefore={<span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>+225</span>}
                    maxLength={10}
                    placeholder="0102030405"
                    style={{ borderRadius: 8 }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="email" label="Email" rules={[{ type: 'email', message: "Format d'email invalide (ex : nom@domaine.ci)" }]} normalize={v => v?.toLowerCase().trim()}>
                  <Input type="email" placeholder="exemple@domaine.com" prefix={<MailOutlined style={{ color: '#bfbfbf' }} />} style={{ borderRadius: 8 }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="adresse" label="Adresse" normalize={v => v?.toUpperCase()}>
                  <Input placeholder="Adresse complete" prefix={<HomeOutlined style={{ color: '#bfbfbf' }} />} style={{ borderRadius: 8 }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="agence" label="Agence" rules={[{ required: true, message: 'Veuillez sélectionner une agence' }]}>
                  <Select placeholder="Sélectionner l'agence" style={{ borderRadius: 8 }} disabled={!canSwitchAgence}>
                    {agences.map(ag => <Option key={ag._id} value={ag._id}>{ag.nom}</Option>)}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }} bodyStyle={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end', gap: 12, flexDirection: isMobile ? 'column' : 'row' }}>
              <Button onClick={() => navigate('/dashboard/clients')} style={{ borderRadius: 8, width: isMobile ? '100%' : 'auto' }}>
                Annuler
              </Button>
              <Button
                type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}
                className="btn-green" style={{ borderRadius: 8, width: isMobile ? '100%' : 'auto', minWidth: 200 }}
              >
                {loading ? 'Enregistrement...' : editing ? 'Mettre a jour' : 'Enregistrer le client'}
              </Button>
            </div>
          </Card>
        </Form>
        </Spin>
      </div>
    </div>
  );
};

export default NouveauClient;
